import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Radio, RadioGroup, FormControlLabel } from '@material-ui/core';
import { Input, Autocomplete, DatePicker, Select } from 'components/FormInputs';
import countries from 'constants/countries';
import { combineName } from 'logic/textTransform';
import { getCompanyTypeOptions } from 'modules/users/constants';

const HostInformationFormLegalSection = ({ user, formik }) => {
  const intl = useIntl();

  const { errors, values, handleChange, setFieldValue, handleBlur } = formik;
  const userVerified =
    !!user.bankid_verified_at || user.kyc_status === 'SUCCEDED' || user.kyc_status === 'PENDING';
  const companyVerified = user.kyc_status === 'SUCCEDED' || user.kyc_status === 'PENDING';
  const dac7article = '168470';

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Select
          name="business_type"
          value={values.business_type}
          onChange={(e) => {
            setFieldValue('business_type', e.target.value);
            if (e.target.value === 'SOLETRADER') {
              const combined = combineName(values.first_name, values.last_name);
              setFieldValue('business_name', combined);
              setFieldValue('owner', combined);
            }
          }}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.companyType" defaultMessage="Company type" />}
          options={getCompanyTypeOptions(intl, values.business_type)}
          disabled={companyVerified}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="business_name"
          value={values.business_name}
          onChange={(e) => {
            setFieldValue('business_name', e.target.value);
            setFieldValue('owner', e.target.value);
          }}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.companyName" defaultMessage="Company name" />}
          disabled={companyVerified || values.business_type === 'SOLETRADER'}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="company_number"
          value={values.company_number}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="Organization number" defaultMessage="Organization number" />}
          disabled={!!user.company_number && companyVerified}
          required
          help={
            window.supportHeroWidget && (
              <>
                <button
                  className="a primary"
                  onClick={() => window.supportHeroWidget.show({ article: dac7article })}
                  type="button"
                >
                  <FormattedMessage id="Click here" defaultMessage="Click here" />
                </button>{' '}
                <FormattedMessage id="label.more.help" defaultMessage="for more information" />
              </>
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="tin_number"
          value={values.tin_number}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={
            <FormattedMessage
              id="Taxpayer Identification Number"
              defaultMessage="Taxpayer Identification Number"
            />
          }
          required
          help={
            window.supportHeroWidget && (
              <>
                <button
                  className="a primary"
                  onClick={() => window.supportHeroWidget.show({ article: dac7article })}
                  type="button"
                >
                  <FormattedMessage id="Click here" defaultMessage="Click here" />
                </button>{' '}
                <FormattedMessage id="label.more.help" defaultMessage="for more information" />
              </>
            )
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2">
          <FormattedMessage id="vat.registered" defaultMessage="Is the company VAT registered?" />
        </Typography>
        <RadioGroup
          row
          name="vat_registered"
          value={values.vat_registered || 'YES'}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
        >
          <FormControlLabel
            value="YES"
            control={<Radio color="primary" />}
            label={intl.formatMessage({ id: 'Yes', defaultMessage: 'Yes' })}
          />
          <FormControlLabel
            value="NO"
            control={<Radio color="primary" />}
            label={intl.formatMessage({ id: 'No', defaultMessage: 'No' })}
          />
        </RadioGroup>
      </Grid>
      {values.vat_registered !== 'NO' && (
        <>
          <Grid item xs={12} sm={6}>
            <Input
              name="vat_number"
              value={values.vat_number}
              onChange={(e) => {
                setFieldValue('vat_number', e.target.value);
                setFieldValue('vat_registered', 'YES');
              }}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Vat number" defaultMessage="Vat number" />}
              help={
                window.supportHeroWidget && (
                  <>
                    <button
                      className="a primary"
                      onClick={() => window.supportHeroWidget.show({ article: dac7article })}
                      type="button"
                    >
                      <FormattedMessage id="Click here" defaultMessage="Click here" />
                    </button>{' '}
                    <FormattedMessage id="label.more.help" defaultMessage="for more information" />
                  </>
                )
              }
              InputProps={{ inputProps: { maxLength: 50 } }}
              required={values.vat_registered !== 'NO'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              name="vat_rate"
              value={values.vat_rate}
              onChange={(e) => {
                e.target.value = (e.target.value || '').replace(/[^0-9|.]/g, '');
                return handleChange(e);
              }}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Vat rate" defaultMessage="Vat rate" />}
              help={
                <FormattedMessage id="vat.rate.help" defaultMessage="Tax rate in percent (0-100)" />
              }
              required={values.vat_registered !== 'NO'}
            />
          </Grid>
        </>
      )}
      <Box mt={2}>&nbsp;</Box>
      <Grid item xs={12}>
        <Typography variant="h6">
          {values.business_type === 'SOLETRADER' ? (
            <FormattedMessage id="soletrader.owner" defaultMessage="Owner" />
          ) : (
            <FormattedMessage id="Board member" defaultMessage="Board member" />
          )}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="first_name"
          value={values.first_name}
          onChange={(e) => {
            setFieldValue('first_name', e.target.value);
            if (values.business_type === 'SOLETRADER' && values.last_name) {
              const combined = combineName(e.target.value, values.last_name);
              setFieldValue('business_name', combined);
              setFieldValue('owner', combined);
            }
          }}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="last_name"
          value={values.last_name}
          onChange={(e) => {
            setFieldValue('last_name', e.target.value);
            if (values.business_type === 'SOLETRADER' && values.first_name) {
              const combined = combineName(values.first_name, e.target.value);
              setFieldValue('business_name', combined);
              setFieldValue('owner', combined);
            }
          }}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          name="birthday"
          value={values.birthday || null}
          onBlur={handleBlur}
          onChange={(date) => {
            if (date) {
              setFieldValue('birthday', date);
            }

            if (date === null) {
              setFieldValue('birthday', null);
            }
          }}
          label={<FormattedMessage id="userform.birthday" defaultMessage="Birthday" />}
          cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
          clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
          errors={errors}
          minDate="1917-01-01"
          maxDate="2017-01-01"
          openTo="year"
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="nationality"
          label={<FormattedMessage id="userform.nationality" defaultMessage="Nationality" />}
          errors={errors}
          value={values.nationality}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('nationality', selected.code);
          }}
          options={countries[intl.locale]}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="country_of_residence"
          label={<FormattedMessage id="userform.country" defaultMessage="Country of residence" />}
          errors={errors}
          value={values.country_of_residence}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('country_of_residence', selected.code);
          }}
          options={countries[intl.locale]}
          required
          disabled={userVerified}
        />
      </Grid>
      <Box mt={2}>&nbsp;</Box>
      <Grid item xs={12}>
        <Typography variant="h6">
          {values.business_type === 'SOLETRADER' ? (
            <FormattedMessage id="soletrader.address" defaultMessage="Owner address" />
          ) : (
            <FormattedMessage id="Address board member" defaultMessage="Address board member" />
          )}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_address1"
          value={values.u_address1}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.address1" defaultMessage="Street address" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_address2"
          value={values.u_address2}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.address2" defaultMessage="Address line 2" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_postal_code"
          value={values.u_postal_code}
          onChange={
            !values.u_country || values.u_country === 'SE'
              ? (e) => {
                  e.target.value = (e.target.value || '').replace(/[^0-9]/g, '');
                  return handleChange(e);
                }
              : handleChange
          }
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.postalCode" defaultMessage="Postal code" />}
          InputProps={{ inputProps: { maxLength: 8 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_city"
          value={values.u_city}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.city" defaultMessage="City" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="u_country"
          label={<FormattedMessage id="userform.country" defaultMessage="Country of residence" />}
          errors={errors}
          value={values.u_country}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('u_country', selected.code);
          }}
          options={countries[intl.locale]}
          required
        />
      </Grid>
      {['CA', 'US', 'MX'].includes(values.u_country) && (
        <Grid item xs={12} sm={6}>
          <Input
            name="u_region"
            value={values.u_region}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            label={<FormattedMessage id="userform.region" defaultMessage="Region" />}
            InputProps={{ inputProps: { maxLength: 40 } }}
            required
          />
        </Grid>
      )}
      <Box mt={2}>&nbsp;</Box>
      <Grid item xs={12}>
        <Typography variant="h6">
          <FormattedMessage id="Company address" defaultMessage="Company address" />
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="hq_address1"
          value={values.hq_address1}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="Street address" defaultMessage="Street address" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="hq_address2"
          value={values.hq_address2}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.address2" defaultMessage="Address line 2" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="hq_postal_code"
          value={values.hq_postal_code}
          onChange={
            !values.hq_country || values.hq_country === 'SE'
              ? (e) => {
                  e.target.value = (e.target.value || '').replace(/[^0-9]/g, '');
                  return handleChange(e);
                }
              : handleChange
          }
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.postalCode" defaultMessage="Postal code" />}
          InputProps={{ inputProps: { maxLength: 8 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="hq_city"
          value={values.hq_city}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={<FormattedMessage id="userform.city" defaultMessage="City" />}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="hq_country"
          label={<FormattedMessage id="userform.country" defaultMessage="Country of residence" />}
          errors={errors}
          value={values.hq_country}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('hq_country', selected.code);
          }}
          options={countries[intl.locale]}
          required
        />
      </Grid>
      {['CA', 'US', 'MX'].includes(values.hq_country) && (
        <Grid item xs={12} sm={6}>
          <Input
            name="hq_region"
            value={values.hq_region}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            label={<FormattedMessage id="userform.region" defaultMessage="Region" />}
            InputProps={{ inputProps: { maxLength: 40 } }}
            required
          />
        </Grid>
      )}
    </>
  );
};

HostInformationFormLegalSection.propTypes = {
  user: PropTypes.shape({
    tin_number: PropTypes.string,
    kyc_status: PropTypes.string,
    bankid_verified_at: PropTypes.string,
    business_type: PropTypes.string,
    company_number: PropTypes.string,
  }),
  formik: PropTypes.shape({
    errors: PropTypes.shape({}),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    values: PropTypes.shape({
      business_type: PropTypes.string,
      business_name: PropTypes.string,
      company_number: PropTypes.string,
      tin_number: PropTypes.string,
      birthday: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      country_of_residence: PropTypes.string,
      nationality: PropTypes.string,
      vat_registered: PropTypes.string,
      vat_number: PropTypes.string,
      vat_rate: PropTypes.string,
      u_address1: PropTypes.string,
      u_address2: PropTypes.string,
      u_city: PropTypes.string,
      u_postal_code: PropTypes.string,
      u_country: PropTypes.string,
      u_region: PropTypes.string,
      hq_address1: PropTypes.string,
      hq_address2: PropTypes.string,
      hq_postal_code: PropTypes.string,
      hq_city: PropTypes.string,
      hq_country: PropTypes.string,
      hq_region: PropTypes.string,
    }),
  }),
};

export default HostInformationFormLegalSection;
